import { useEffect, useState } from 'react';
import { addProductCategoriesOptionRequest, getProductCategoriesOptionsRequest, ProductCategoriesLoadingKeys } from '../../slices/productCategoriesSlice';
import { useDispatch, useSelector } from 'react-redux';
import { ValueSelectByObject } from '../ValueSelectByObject';
import { Controller, useForm } from 'react-hook-form';
import { createNewGroup, getSuppliers, ProductGroupLoadingKeys } from '../../slices/productGroupSlice';
import { SelectDropdown } from '../SelectDropdown';

export const CreateProductGroupSidebar = ({ 
    formId,
    access,
    onSuccessSubmit
}) => {

    const { control, register, handleSubmit, setValue } = useForm({
        defaultValues: {
            productName: "",
            category: "",
            leadTime: 40
        },
    });
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedImage, setSelectedImage] = useState("");
    const dispatch = useDispatch();
    const {
        loading: groupLoading,
        loadingKeys: groupLoadingKeys,
        vendorsToSelect,
    } = useSelector((state) => state.productGroup);
    const {
        loading: productCategoryLoading,
        loadingKeys: productCategoryKeys,
        options: productCategoriesOptions
    } = useSelector((state) => state.productCategories);

    useEffect(() => {
        dispatch(getProductCategoriesOptionsRequest({}))
        dispatch(getSuppliers({}))
    }, []);

    const onSubmit = async (data) => {
        const dataForCreation = {
            lead_time: data?.leadTime || null,
            name: data?.productName || "",
            category_group_id: data?.category || null,
            vendor_id: selectedSupplier || null
        }
        const res = dispatch(createNewGroup({ creationData: dataForCreation })).unwrap();
        res?.then(() => {
            onSuccessSubmit?.();
        });
    }
    const getSuppliersToSelectIsLoading = () =>
        groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.getSuppliers);
    const productCategoriesIsLoading = () => 
        productCategoryLoading && productCategoryKeys.includes(ProductCategoriesLoadingKeys.getProductCategoriesOptions) || 
        productCategoryLoading && productCategoryKeys.includes(ProductCategoriesLoadingKeys.addProductCategoriesOptions);

    return (
        <div>
            <form onSubmit={handleSubmit(onSubmit)} id={formId}>
                <div className="mb-4">
                    <div className="w-full">
                        {selectedImage ? (
                            <div className="rounded-md overflow-hidden border border-gray-200 bg-white shadow-sm p-4 flex items-center justify-center min-h-[300px]">
                                <img
                                    src={selectedImage}
                                    alt={"Image by group"}
                                    className="w-full h-full object-contain"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = 'https://placehold.co/300x200/e6e6e6/cccccc?text=No+Image';
                                    }}
                                />
                            </div>
                        ) : (
                            <div className="rounded-md overflow-hidden border border-gray-200 bg-white shadow-sm p-4 flex items-center justify-center min-h-[300px]">
                                <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                        )}
                    </div>
                </div>
                <div className="border-t border-gray-200 pt-4 mt-2">
                    <dl className="divide-y divide-gray-200">
                        <div className="py-3 flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">Name</dt>
                            <dd className="text-sm text-gray-900">
                                <div className="relative">
                                    <div className='flex items-center gap-2'>
                                        <input
                                            id="productName"
                                            {...register("productName")}
                                            className="w-52 bg-white border border-gray-300 rounded focus:ring-0 text-sm text-gray-600 py-1 px-2 disabled:bg-gray-200"
                                        />
                                    </div>
                                </div>
                            </dd>
                        </div>
                        <div className="py-3 flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">Category</dt>
                            <dd className="text-sm text-gray-900">
                                <Controller
                                    id="category"
                                    name="category"
                                    control={control}
                                    defaultValue={null}
                                    render={({ field }) => (
                                        <ValueSelectByObject
                                            key={field.value}
                                            selectedValue={field.value}
                                            selectClassName={"w-52"}
                                            isLoading={productCategoriesIsLoading()}
                                            createNew={async (inputValue) => {
                                                await dispatch(addProductCategoriesOptionRequest({
                                                    value: inputValue
                                                })).unwrap();                      
                                            }}
                                            options={productCategoriesOptions}
                                            onSelect={(item) => {
                                                const categoryId = item?.value;
                                                field.onChange(categoryId);
                                            }}
                                        />
                                    )}
                                />
                            </dd>
                        </div>
                        <div className="py-3 flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">Lead Time</dt>
                            <dd className="text-sm text-gray-900">
                                <div className="relative">
                                    <div className='flex items-center gap-2'>
                                        <input
                                            {...register("leadTime", {
                                                onChange: (e) => {
                                                    let newValue = e.target.value;
                                                    // Allow only digits
                                                    if (/^\d*$/.test(newValue)) {
                                                        // Remove leading zeros
                                                        if (newValue.startsWith('0') && newValue.length > 1) {
                                                            newValue = newValue.replace(/^0+/, '');
                                                        }
                                                        setValue("leadTime", newValue);
                                                    }
                                                }
                                            })}
                                            className="w-52 bg-white border border-gray-300 rounded focus:ring-0 text-sm text-gray-600 py-1 px-2 disabled:bg-gray-200"
                                        />
                                    </div>
                                </div>
                            </dd>
                        </div>
                        <div className="py-3 flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">Supplier</dt>
                            <dd className="text-sm text-gray-900 w-52">
                                <SelectDropdown
                                    key={selectedSupplier}
                                    disabled={getSuppliersToSelectIsLoading()}
                                    options={vendorsToSelect}
                                    value={selectedSupplier}
                                    onChange={setSelectedSupplier}
                                    selectClassname='!bg-white !px-2 !py-1 !text-sm !rounded'
                                    selectedValueClassname='truncate'
                                    itemValueClassname='!text-sm'
                                />
                            </dd>
                        </div>
                    </dl>
                </div>
            </form>
        </div>
    );
};
